import React from 'react'
import {
  StreamEventType,
  StreamMessage,
  ToolCall,
  ToolName,
} from 'src/agent/types/streamingTypes'

import { ChatMarkdown } from '../AgentChat/AgentChat.style'
import styles from './StreamMessageRenderer.style'
import TaskRenderer from './TaskRenderer'
import TodoListRenderer from './TodoListRenderer'

interface StreamMessageRendererProps {
  message: StreamMessage
  isLatestMessage?: boolean // Whether this is the most recent message in the stream
}

/**
 * Main renderer for streaming agent messages
 * Routes different event types to specialized renderers
 */
const StreamMessageRenderer: React.FC<StreamMessageRendererProps> = ({
  message,
  isLatestMessage = false,
}) => {
  const { eventType, content, toolCalls } = message

  // TODO: feature-pods-ai - review the new components in this folder for design system alignment
  switch (eventType) {
    case StreamEventType.AI_EVENT:
      // Filter out redundant "Updated todo list" messages since we have TodoListRenderer
      // Matches: "Updated todo list to [{'content': ..., 'status': ...}]"
      if (content?.match(/^Updated todo list to \[\{/i)) {
        return null
      }

      // Render AI-generated markdown content
      return (
        <div className="message-content">
          <ChatMarkdown>{content || ''}</ChatMarkdown>
        </div>
      )

    case StreamEventType.TOOL_EVENT:
      // Render all TOOL_EVENTs as TaskRenderer cards
      return (
        <div className="message-content">
          <TaskRenderer
            title="Task Result"
            description="" // No description for results
            content={content}
            isLoading={false}
          />
        </div>
      )

    case StreamEventType.TOOL_START:
      // Render specialized UI for different tool types
      return (
        <div className="message-content">
          {toolCalls?.map((toolCall, idx) => (
            <ToolCallRenderer
              key={`${toolCall.id}-${idx}`}
              toolCall={toolCall}
              isLatestMessage={isLatestMessage}
            />
          ))}
        </div>
      )

    case StreamEventType.DONE:
      return <></>

    case StreamEventType.ERROR:
      return (
        <div css={styles} className="StreamMessageRenderer">
          <div className="error-section">
            <div className="error-message">{content}</div>
          </div>
        </div>
      )

    default:
      return (
        <div className="message-content">
          <ChatMarkdown>{content || ''}</ChatMarkdown>
        </div>
      )
  }
}

/**
 * Routes tool calls to specialized renderers based on tool name
 */
const ToolCallRenderer: React.FC<{
  toolCall: ToolCall
  isLatestMessage?: boolean
}> = ({ toolCall, isLatestMessage = false }) => {
  if (toolCall.name === ToolName.WRITE_TODOS) {
    return <TodoListRenderer toolCall={toolCall} />
  }

  if (toolCall.name === ToolName.TASK) {
    const taskCall = toolCall as any // TaskToolCall type
    const isRunning = isLatestMessage
    return (
      <TaskRenderer
        title={isRunning ? 'Running Task' : 'Task Complete'}
        description={taskCall.args.description}
        subagentType={taskCall.args.subagent_type}
        isLoading={isRunning}
      />
    )
  }

  // Fallback for unknown tool types
  return (
    <div style={{ color: 'red', fontStyle: 'italic' }}>
      <code>{toolCall.name}</code>
    </div>
  )
}

export default StreamMessageRenderer
